import React from 'react'
import moment from 'moment'
import { escapeRegExp } from 'lodash'
import { IconName, Text } from '@components/base'
import { ChatbotConversation, HealthDataReport, HealthDataReportStateKind } from '@src/types'
import { SHORT_MONTH_AND_DATE_FORMAT } from '@config/momentFormat.ts'

type ConversationBuckets = {
  today: ChatbotConversation[]
  yesterday: ChatbotConversation[]
  lastWeek: ChatbotConversation[]
  lastMonth: ChatbotConversation[]
  older: ChatbotConversation[]
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const dayMilliseconds = 1000 * 60 * 60 * 24
const daysBetween = (a: Date, b: Date) =>
  Math.floor((startOfDay(a).getTime() - startOfDay(b).getTime()) / dayMilliseconds)

export const groupChatbotConversations = (conversations: ChatbotConversation[]) => {
  const now = new Date()
  const todayStart = startOfDay(now)

  const buckets: ConversationBuckets = {
    today: [],
    yesterday: [],
    lastWeek: [],
    lastMonth: [],
    older: [],
  }

  for (const conversation of conversations) {
    const created = new Date(conversation.createdAt)
    const diffDays = daysBetween(todayStart, created)

    if (diffDays === 0) {
      buckets.today.push(conversation)
    } else if (diffDays === 1) {
      buckets.yesterday.push(conversation)
    } else if (diffDays <= 7) {
      buckets.lastWeek.push(conversation)
    } else if (diffDays <= 30) {
      buckets.lastMonth.push(conversation)
    } else {
      buckets.older.push(conversation)
    }
  }

  return [
    { label: 'Today', conversations: buckets.today },
    { label: 'Yesterday', conversations: buckets.yesterday },
    { label: 'Previous 7 Days', conversations: buckets.lastWeek },
    { label: 'Previous Month', conversations: buckets.lastMonth },
    { label: 'Older', conversations: buckets.older },
  ].filter(({ conversations }) => conversations.length > 0)
}

export const formatReportDateRange = (report: HealthDataReport) => {
  const startDate = moment(report.startDate).format(SHORT_MONTH_AND_DATE_FORMAT)
  const endDate = moment(report.endDate).format(SHORT_MONTH_AND_DATE_FORMAT)

  return startDate === endDate ? startDate : `${startDate} - ${endDate}`
}

export const getConversationIcon = (conversation: ChatbotConversation): IconName => {
  if (conversation.pinned) {
    return 'push-pin'
  }
  if (conversation.reportId) {
    return 'chart-line-up'
  }
  return 'chat'
}

export const isReportUnread = (report: HealthDataReport) => {
  return !report.readAt && report.lastState.kind === HealthDataReportStateKind.Completed
}

export const stripMarkdown = (text: string) =>
  text
    .replace(/#{1,6}\s+/g, '') // headings
    .replace(/\*\*(.+?)\*\*/g, '$1') // bold
    .replace(/\*(.+?)\*/g, '$1') // italic
    .replace(/__(.+?)__/g, '$1') // bold
    .replace(/_(.+?)_/g, '$1') // italic
    .replace(/\[(.+?)]\(.+?\)/g, '$1') // links
    .replace(/`(.+?)`/g, '$1') // inline code
    .replace(/^\s*[-*+]\s+/gm, '') // unordered list markers
    .replace(/^\s*\d+\.\s+/gm, '') // ordered list markers
    .replace(/\n{2,}/g, ' ') // collapse multiple newlines
    .replace(/\n/g, ' ') // remaining newlines
    .trim()

export const renderHighlightedText = (
  text: string,
  query: string | undefined,
  type: 'large' | 'small',
  highlightStyle: object,
) => {
  if (!query) {
    return text
  }
  // eslint-disable-next-line security/detect-non-literal-regexp -- query is escaped via lodash escapeRegExp
  const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi')
  return text.split(regex).map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <Text key={index} type={type} bold style={highlightStyle}>
        {part}
      </Text>
    ) : (
      part
    ),
  )
}
